/**
 * "Auftrag übernehmen" with a confirm step. The dialog shows the job's own
 * notes (the advisor's intake note, above all) so the technician reads what
 * the customer said before committing to the car. Button content + styling
 * are passed in so it matches each host (Today card, job page).
 *
 * There is one claim now: the whole job — every loose service on it, and
 * the job itself when nobody owns it yet. The per-step and per-service
 * variants went with tasks per service (2026-09-03).
 */
import { useState, type MouseEvent, type ReactNode } from 'react'
import type { WoDetailNote } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { noteAuthorLabel } from '@/lib/format'
import { ConfirmDialog } from '@/components/ConfirmDialog'

interface Props {
  /** The claim action (fires on confirm). */
  readonly onConfirm: () => void
  /** The job's notes, shown in the dialog before the tech commits. */
  readonly seededNotes?: readonly WoDetailNote[]
  readonly className: string
  readonly disabled?: boolean
  readonly children: ReactNode
  /** e.g. stopPropagation so a row-level click handler doesn't also fire. */
  readonly onClickCapture?: (e: MouseEvent) => void
}

export function ClaimConfirmButton({
  onConfirm,
  seededNotes = [],
  className,
  disabled,
  children,
  onClickCapture,
}: Props) {
  const { t } = useI18n()
  const [open, setOpen] = useState(false)
  const notes = seededNotes

  return (
    <>
      <button
        type="button"
        disabled={disabled}
        onClick={(e) => {
          onClickCapture?.(e)
          setOpen(true)
        }}
        className={className}
      >
        {children}
      </button>
      {open && (
        // The dialog portals to <body>, but React bubbles synthetic events
        // through the REACT tree, not the DOM one — so without this, confirming
        // a claim also fired the host card's row click and navigated away to
        // the work order. The tech asked to take a job, not to leave the board.
        <div onClick={(e) => e.stopPropagation()}>
          <ConfirmDialog
            title={t.confirm.claimJobTitle}
            detail={notes.length > 0 ? <NotesPreview notes={notes} label={t.workOrder.jobNotes} youLabel={t.tasks.you} /> : undefined}
            confirmLabel={t.confirm.claimJobCta}
            tone="primary"
            onConfirm={onConfirm}
            onClose={() => setOpen(false)}
          />
        </div>
      )}
    </>
  )
}

function NotesPreview({ notes, label, youLabel }: { readonly notes: readonly WoDetailNote[]; readonly label: string; readonly youLabel: string }) {
  return (
    <div>
      <div className="mb-1.5 text-10-5 font-bold uppercase tracking-mk-up12 text-glass-text-muted">
        {label} ({notes.length})
      </div>
      <ul className="flex max-h-48 flex-col gap-1.5 overflow-y-auto scrollbar-slim">
        {notes.map((n) => (
          <li key={n.id} className="flex items-start gap-2 rounded-lg bg-glass-fill-strong px-2.5 py-1.5">
            <span className="shrink-0 pt-0.5 font-mono text-11 text-glass-text-muted">{n.at}</span>
            <span className="text-13 text-glass-text">
              <span className="font-semibold text-glass-text-muted">{noteAuthorLabel(n, youLabel)} · </span>
              {n.body}
            </span>
          </li>
        ))}
      </ul>
    </div>
  )
}
